import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

/**
 * LoadIdentityPage - Called immediately after SAML login redirect.
 * 
 * Flow:
 * 1. User logs in via /api/saml/login
 * 2. IdP redirects to /api/saml/assert 
 * 3. API sets auth cookie and redirects to /load-identity
 * 4. This component loads the user identity and navigates to intended destination
 */
export function LoadIdentityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loadIdentity, error } = useAuthStore();

  useEffect(() => {
    const loadAndRedirect = async () => {
      await loadIdentity();
      
      // After loading identity, redirect to the intended page
      const state = window.history.state as { from?: string };
      const from = state?.from || '/';
      
      navigate(from, { replace: true });
    };

    loadAndRedirect();
  }, [loadIdentity, navigate]);

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <h2>{t('auth.authenticationError')}</h2>
        <p>{error}</p>
        <button onClick={() => window.location.href = '/'}>
          {t('auth.returnHome')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      flexDirection: 'column',
      gap: '1rem'
    }}>
      <div style={{ 
        border: '4px solid var(--color-spinner-track)',
        borderTop: '4px solid var(--color-spinner)',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        animation: 'spin 1s linear infinite'
      }} />
      <p>{t('auth.loadingProfile')}</p>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
